import React from 'react';
import { motion } from 'framer-motion';

/**
 * Oblique AI Icon — Official 6-petal geometric pinwheel
 * Interactive rotation, optical facet shading, and spring micro-interactions on hover.
 */
const PETAL_PATHS = [
    { d: "M53.78,97.92l-31.06-17.84-20.34-11.86c-3.13-1.82-3.21-7.86-.02-9.72l40.52-23.66,18.28-10.71c1.75-1.02,4.17-1.08,5.97.07,1.25.8,3.08,2.44,3.07,4.53l-.15,56.55-.23,12.87c-.04,2.05-2.41,3.8-3.67,4.41-2.5.87-4.58.28-6.56-1.05l-5.8-3.58Z", opacity: 0.92 },
    { d: "M98.84,210.98l-30.65-17.93-27.72-16.18c-1.91-1.12-2.8-3.05-2.92-4.97s.89-3.94,2.71-5.03l9.04-5.43,48.95-28.26c1.9-1.1,4.05-1.19,5.95-.16,1.43.77,3.32,2.4,3.33,4.56l.19,27.44.14,40.83c0,2.17-1.49,3.97-3,4.91-1.67,1.04-3.97,1.42-6.02.22Z", opacity: 0.82 },
    { d: "M148.8,45.37l-36.3,20.97-21.17,12.33c-1.93.99-3.73,1.35-6.03.4-.66-.59-3.39-2.25-3.4-4.24l-.1-70.02c1.08-3.72,3.96-5.36,7.6-4.64l5.5,3.25,54.1,31.37c1.84,1.06,2.76,3.69,2.79,5.21.04,1.98-.87,4.14-3,5.37Z", opacity: 0.98 },
    { d: "M27.1,161.07l-11.46,6.95c-2.11.81-4.38.68-6.02-.6-1.89-1.48-2.68-3.25-2.6-5.77l.11-3.22.02-65.16c.9-3.53,4.25-5.58,7.77-4.69l16.25,9.64,41.97,24.55c2.14,1.25,3.54,2.92,3.62,5.29s-.9,4.63-3.15,5.94l-46.51,27.08Z", opacity: 0.88 },
    { d: "M186.71,153.41l-13.27,7.89-45.32,26.34c-3.74,1.47-7.33.08-8.65-3.8l.03-70.11c.72-1.77,1.93-3.41,3.08-4.02,1.77-.94,4.02-1.11,5.73-.13l34.85,20.17,22.35,12.9c1.97,1.14,3.51,2.62,3.71,4.61.22,2.33-.2,4.79-2.5,6.15Z", opacity: 0.95 },
    { d: "M131.7,98l-15.52-9.18c-1.97-1.17-3.45-2.99-3.49-5.31-.03-1.99,1.16-4.37,3.07-5.49l20-11.81,36.45-21.38c2.42-1.42,4.76-2.14,7.11-.65s2.99,3.64,2.99,6.53v64.31c0,2.61.3,4.61-1.75,6.52-1.31,1.22-4.15,2.53-6.32,1.26l-42.52-24.81Z", opacity: 0.85 }
];

const ObliqueAIIcon = ({ 
    size = 22, 
    isHovered = false, 
    className = '', 
    color = 'currentColor' 
}) => {
    return (
        <motion.svg
            width={size}
            height={size}
            viewBox="0 0 189.27 211.72"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            className={`shrink-0 select-none ${className}`}
            animate={{ 
                rotate: isHovered ? 360 : 0,
                scale: isHovered ? 1.05 : 1
            }}
            transition={isHovered 
                ? { 
                    rotate: { duration: 3.5, ease: 'linear', repeat: Infinity },
                    scale: { duration: 0.25, ease: 'easeOut' }
                  } 
                : { 
                    rotate: { duration: 0.5, ease: [0.16, 1, 0.3, 1] },
                    scale: { duration: 0.3, ease: 'easeOut' }
                  }
            }
            style={{ 
                display: 'block',
                transformOrigin: '94.635px 105.86px' 
            }}
        >
            {PETAL_PATHS.map((petal, i) => (
                <motion.path
                    key={i}
                    d={petal.d}
                    fill={color}
                    initial={{ opacity: petal.opacity }}
                    animate={isHovered 
                        ? { 
                            opacity: [petal.opacity, 1, petal.opacity],
                            scale: [1, 1.03, 1]
                          }
                        : { 
                            opacity: petal.opacity,
                            scale: 1 
                          }
                    }
                    transition={isHovered 
                        ? { 
                            duration: 1.2, 
                            repeat: Infinity, 
                            delay: i * 0.12, 
                            ease: 'easeInOut' 
                          } 
                        : { duration: 0.3 }
                    }
                    style={{ transformOrigin: '94.635px 105.86px' }}
                />
            ))}
        </motion.svg>
    );
};

export default ObliqueAIIcon;
